import React, { useState, useEffect, useCallback } from 'react';
import { IEditorProps } from './props-interfaces';
import { config } from 'config';
import { isJson, RsMime } from 'data/mime-type';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import AceEditor from 'react-ace';
import { config as aceConfig } from 'ace-builds';

import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-css";
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/mode-handlebars";
import "ace-builds/src-noconflict/theme-tomorrow";
import 'ace-builds/src-min-noconflict/ext-searchbox';

aceConfig.set(
    "basePath",
    "https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/"
  );
aceConfig.setModuleUrl(
    "ace/mode/javascript_worker",
    "https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/worker-javascript.js"
);
aceConfig.setModuleUrl(
    "ace/mode/json_worker",
    "https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/worker-json.js"
);
aceConfig.setModuleUrl(
    "ace/mode/css_worker",
    "https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/worker-css.js"
);
aceConfig.setModuleUrl(
    "ace/mode/html_worker",
    "https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/worker-html.js"
);

const MemoEditor = React.memo(AceEditor);

const mimeTypeToMode = (mimeType: string) => {
    if (isJson(mimeType)) {
        return "json";
    }
    const ext = new RsMime().getExtension(mimeType);
    switch (ext) {
        case "css":
            return "css";
        case "js":
            return "javascript";
        case "html":
            return "html";
        case "scss":
            return "sass";
        case "njk":
            return "handlebars"
    }
    return "javascript";
}

export const CodeEditor = (props: IEditorProps) => {
    const {
        path, mimeType, onDirty, onDirChange, text, operation, options, service
    } = props;
    const [ currentText, setCurrentText ] = useState('');
    const [ dirty, setDirty ] = useState(false);
    const [ currentMimeType, setCurrentMimeType ] = useState('');
    const [ currentPath, setCurrentPath ] = useState(path);
    const history = useHistory();
    const filename = _.last(currentPath.split('/')) || '';
    const isNew = _.last(path.split('/')) === '';

    const currentOptions = {
        fullPath: false,
        readOnlyPath: !isNew,
        createLocalPath: '',
        deleteButton: false,
        ...options
    };

    useEffect(() => {
        setCurrentMimeType(mimeType);
    }, [ mimeType ]);

    useEffect(() => {
        let currText = text;
        if (text && isJson(mimeType)) {
            try {
                const obj = JSON.parse(text);
                currText = JSON.stringify(obj, null, 4); // formats JSON
            } catch {}
        }
        setCurrentText(currText);
    }, [ text ]);

    useEffect(() => {
        setCurrentPath(path);
        setDirty(false);
    }, [ path ]);

    const onDoDirty = useCallback((isDirty: boolean) => {
        onDirty(isDirty);
        setDirty(isDirty);
    }, [onDirty]);

    useEffect(() => {
        if (text) return;

        if (operation !== 'new') {
          config.api.fetchText(path)
            .then(([text, fetchedMimeType]) => {
                if (isJson(fetchedMimeType) && text) {
                    const obj = JSON.parse(text);
                    text = JSON.stringify(obj, null, 4); // formats JSON
                }
                setCurrentText(text);
                setCurrentMimeType(fetchedMimeType);
            })
            .catch((err) => {
                alert('loading code failed');
                console.log('loading code failed: ' + err);
            });
        } else {
            setCurrentText('');
        }

        onDoDirty(false);
    }, [ path, operation, text ]);

    const onDoSubmit = async () => {
        if (!currentPath || currentPath.endsWith('/')) {
            alert('Please enter the item name in the top box');
            return;
        }
        const submitMimeType = isJson(currentMimeType) ? 'application/json' : currentMimeType;
        const res = await config.api.saveItem(currentPath, currentText, submitMimeType);
        if (res) {
            alert(res);
            return;
        }
        onDoDirty(false);
        if (path !== currentPath) {
            history.replace('/' + currentPath);
        }
        if (service && service.name === 'services') {
            // reload everything for services as the list of services may have changed
            document.location.reload();
        }
    };

    const onValueChange = useCallback((text: string) => {
        setCurrentText(text);
        onDoDirty(true);
    }, [onDoDirty]);

    const onFilenameChange = (ev: React.ChangeEvent) => {
        const newPath = ev.target['value'] as string;
        if (currentOptions.fullPath) {
            setCurrentPath(newPath);
        } else {
            const pathParts = currentPath.split('/');
            setCurrentPath([ ...pathParts.slice(0, -1), newPath ].join('/'));
        }
        onDoDirty(true);
        if (newPath.indexOf('.') >= 0) {
            const ext = _.last(newPath.split('.')) || '';
            const newMimeType = config.mime.getType(ext);
            console.log('new mime type: ' + newMimeType);
            newMimeType && setCurrentMimeType(newMimeType);
        }
    };

    const onCreateLocal = async () => {
        try {
            await config.api.saveItem(currentOptions.createLocalPath, '', mimeType);
            onDirChange();
        } catch(err) {
            console.log('Failed to create local config');
        }
    };

    const onDelete = async () => {
        try {
            if (!window.confirm('Are you sure you want to delete this config file?')) return;
            
            await config.api.deleteItem(currentPath);
            onDirChange();
        } catch(err) {
            console.log('Failed to delete local config');
        }
    };

    const filenameClass = currentOptions.readOnlyPath ? 'filename read-only' : 'filename';

    return (
        <>
            <div className={filenameClass}>
                <input type='text' readOnly={currentOptions.readOnlyPath} value={currentOptions.fullPath ? path : filename} onChange={onFilenameChange} />
            </div>
            <div className='editor-wrapper'>
                <MemoEditor
                    mode={mimeTypeToMode(currentMimeType)}
                    theme="tomorrow"
                    value={currentText}
                    onChange={onValueChange}
                    name="ace-editor"
                    height="100%"
                    width="100%"
                    />
            </div>
            <div className={"jd-submit-button-container " + (dirty ? "sf-dirty" : "")}>
                {currentOptions.createLocalPath && <button type="button" className="jd-button" onClick={onCreateLocal}>
                    Create Local
                </button>}
                <button type="button" className="jd-submit-button" onClick={onDoSubmit}>
                    Submit
                </button>
                {currentOptions.deleteButton && <button type="button" className="jd-button" onClick={onDelete}>
                    Delete
                </button>}
            </div>
        </>
    );
}